import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { formatCurrency, formatAxisShort } from "@/lib/format";

const MIN_WINDOW = 3; // months
const TOTAL_MONTHS = 24;
const RED = "#e10600";
const GOLD = "#d4af37";
const SILVER = "#e8e8e8";

function SingleSeriesTooltip({ active, payload, label, color, seriesLabel, monthLabelFn }) {
  if (!active || !payload || payload.length === 0) return null;
  const v = payload[0]?.value;
  return (
    <div
      data-testid="chart-tooltip"
      style={{
        background: "rgba(15,15,15,0.96)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 12,
        padding: "8px 12px",
        backdropFilter: "blur(8px)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        minWidth: 200,
      }}
    >
      <div style={{ color: "#cfcfcf", fontSize: 11, letterSpacing: 1.6, textTransform: "uppercase", marginBottom: 4 }}>
        {monthLabelFn(label)}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14 }}>
        <span style={{ color, fontSize: 12 }}>{seriesLabel}</span>
        <span style={{ color: "#fff", fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
          {formatCurrency(v)}
        </span>
      </div>
    </div>
  );
}

function ZoomRangeSlider({ value, onChange, max }) {
  return (
    <SliderPrimitive.Root
      data-testid="chart-zoom-range"
      className="relative flex w-full touch-none select-none items-center h-6"
      value={value}
      min={0}
      max={max}
      step={1}
      minStepsBetweenThumbs={MIN_WINDOW}
      onValueChange={onChange}
    >
      <SliderPrimitive.Track
        className="relative grow rounded-full"
        style={{ height: 4, background: "rgba(255,255,255,0.08)" }}
      >
        <SliderPrimitive.Range
          className="absolute rounded-full"
          style={{
            height: 4,
            background: "linear-gradient(90deg, #e10600 0%, #ff3a35 100%)",
            boxShadow: "0 0 12px rgba(225,6,0,0.45)",
          }}
        />
      </SliderPrimitive.Track>
      {[0, 1].map((i) => (
        <SliderPrimitive.Thumb
          key={i}
          className="block rounded-full focus:outline-none"
          style={{
            width: 16,
            height: 16,
            background: "#fff",
            border: "2px solid #e10600",
            boxShadow: "0 0 0 4px rgba(225,6,0,0.18), 0 2px 8px rgba(0,0,0,0.5)",
            cursor: "grab",
          }}
        />
      ))}
    </SliderPrimitive.Root>
  );
}

function MiniChart({ data, dataKey, color, title, gradientId, testid, t }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl px-4 py-4"
      style={{
        background: "rgba(0,0,0,0.30)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: color,
            boxShadow: `0 0 8px ${color}`,
          }}
        />
        <h4 className="text-sm font-medium" style={{ color: "#e8e8e8", letterSpacing: "-0.01em" }}>
          {title}
        </h4>
      </div>
      <div style={{ width: "100%", height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 14, bottom: 4, left: 4 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.42} />
                <stop offset="100%" stopColor={color} stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis
              dataKey="month"
              stroke="#777"
              tick={{ fill: "#888", fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
            />
            <YAxis
              tick={{ fill: "#888", fontSize: 10 }}
              tickFormatter={formatAxisShort}
              stroke="#777"
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.06)" }}
              width={58}
            />
            <Tooltip
              content={
                <SingleSeriesTooltip
                  color={color}
                  seriesLabel={title}
                  monthLabelFn={(lbl) => t("calculator.tooltip_month", { count: lbl })}
                />
              }
              cursor={{ stroke: "rgba(255,255,255,0.10)" }}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 3.5, fill: color, stroke: "#0a0a0a", strokeWidth: 2 }}
              isAnimationActive={true}
              animationDuration={600}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function ProjectionChart({ months }) {
  const { t } = useTranslation();

  // shared visible window for all 3 charts
  const [window, setWindow] = useState([0, TOTAL_MONTHS]);
  const containerRef = useRef(null);

  // pointer drag-to-pan state (applies to ALL charts via the outer wrapper)
  const dragRef = useRef({
    active: false,
    startX: 0,
    startWindow: [0, TOTAL_MONTHS],
    pointerId: null,
  });
  const activePointersRef = useRef(new Set());
  const pinchRef = useRef({
    active: false,
    initialDistance: 0,
    initialWindow: [0, TOTAL_MONTHS],
    centerMonth: 0,
  });

  const windowWidth = window[1] - window[0];
  const isZoomed = windowWidth < TOTAL_MONTHS;

  const visibleData = useMemo(
    () => (months || []).slice(window[0], window[1] + 1),
    [months, window]
  );

  const setClampedWindow = ([s, e]) => {
    let start = Math.round(s);
    let end = Math.round(e);
    if (end - start < MIN_WINDOW) {
      const mid = Math.round((start + end) / 2);
      start = Math.max(0, mid - Math.floor(MIN_WINDOW / 2));
      end = Math.min(TOTAL_MONTHS, start + MIN_WINDOW);
      start = Math.max(0, end - MIN_WINDOW);
    }
    if (start < 0) {
      end -= start;
      start = 0;
    }
    if (end > TOTAL_MONTHS) {
      start -= end - TOTAL_MONTHS;
      end = TOTAL_MONTHS;
    }
    start = Math.max(0, start);
    end = Math.min(TOTAL_MONTHS, end);
    setWindow([start, end]);
  };

  const zoomIn = () => {
    const w = window[1] - window[0];
    const target = Math.max(MIN_WINDOW, Math.round(w * 0.6));
    const mid = Math.round((window[0] + window[1]) / 2);
    setClampedWindow([mid - Math.floor(target / 2), mid + Math.ceil(target / 2)]);
  };

  const zoomOut = () => {
    const w = window[1] - window[0];
    const target = Math.min(TOTAL_MONTHS, Math.round(w / 0.6));
    const mid = Math.round((window[0] + window[1]) / 2);
    setClampedWindow([mid - Math.floor(target / 2), mid + Math.ceil(target / 2)]);
  };

  const reset = () => setWindow([0, TOTAL_MONTHS]);

  const onPointerDown = (e) => {
    activePointersRef.current.add(e.pointerId);
    if (activePointersRef.current.size > 1) {
      dragRef.current.active = false;
      return;
    }
    if (e.pointerType === "touch" && pinchRef.current.active) return;
    if (!isZoomed) return;
    dragRef.current.active = true;
    dragRef.current.startX = e.clientX;
    dragRef.current.startWindow = [...window];
    dragRef.current.pointerId = e.pointerId;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {}
  };

  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    if (activePointersRef.current.size > 1) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const dxPx = e.clientX - dragRef.current.startX;
    const monthsPerPx = (dragRef.current.startWindow[1] - dragRef.current.startWindow[0]) / rect.width;
    const dxMonths = Math.round(-dxPx * monthsPerPx);
    const ws = dragRef.current.startWindow[0] + dxMonths;
    const we = dragRef.current.startWindow[1] + dxMonths;
    setClampedWindow([ws, we]);
  };

  const onPointerUp = (e) => {
    activePointersRef.current.delete(e.pointerId);
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    try {
      if (dragRef.current.pointerId !== null) {
        e.currentTarget.releasePointerCapture(dragRef.current.pointerId);
      }
    } catch (_) {}
    dragRef.current.pointerId = null;
  };

  // Touch pinch (two-finger)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const dist = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        pinchRef.current.active = true;
        pinchRef.current.initialDistance = dist(e.touches[0], e.touches[1]);
        pinchRef.current.initialWindow = [...window];
        pinchRef.current.centerMonth = Math.round((window[0] + window[1]) / 2);
        dragRef.current.active = false;
      }
    };
    const handleTouchMove = (e) => {
      if (pinchRef.current.active && e.touches.length === 2) {
        e.preventDefault();
        const newDist = dist(e.touches[0], e.touches[1]);
        const ratio = pinchRef.current.initialDistance / Math.max(1, newDist);
        const initW = pinchRef.current.initialWindow[1] - pinchRef.current.initialWindow[0];
        const newW = Math.max(MIN_WINDOW, Math.min(TOTAL_MONTHS, Math.round(initW * ratio)));
        const mid = pinchRef.current.centerMonth;
        setClampedWindow([mid - Math.floor(newW / 2), mid + Math.ceil(newW / 2)]);
      }
    };
    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) pinchRef.current.active = false;
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    el.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window[0], window[1]]);

  return (
    <div data-testid="projection-chart" className="w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "#9a9a9a" }}>
            {t("calculator.chart_eyebrow")}
          </div>
          <h3 className="text-lg sm:text-xl font-medium mt-1" style={{ color: "#f4f4f4", letterSpacing: "-0.01em" }}>
            {t("calculator.chart_title")}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-xs px-3 py-1.5 rounded-full"
            data-testid="chart-window-label"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#cfcfcf",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {t("calculator.chart_window_label", { start: window[0], end: window[1] })}
          </span>
          <button
            type="button"
            data-testid="chart-zoom-out"
            onClick={zoomOut}
            disabled={windowWidth >= TOTAL_MONTHS}
            className="p-2 rounded-md transition-all"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#cfcfcf",
              opacity: windowWidth >= TOTAL_MONTHS ? 0.4 : 1,
              cursor: windowWidth >= TOTAL_MONTHS ? "not-allowed" : "pointer",
            }}
            aria-label="Zoom out"
          >
            <Minus size={14} />
          </button>
          <button
            type="button"
            data-testid="chart-zoom-in"
            onClick={zoomIn}
            disabled={windowWidth <= MIN_WINDOW}
            className="p-2 rounded-md transition-all"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#cfcfcf",
              opacity: windowWidth <= MIN_WINDOW ? 0.4 : 1,
              cursor: windowWidth <= MIN_WINDOW ? "not-allowed" : "pointer",
            }}
            aria-label="Zoom in"
          >
            <Plus size={14} />
          </button>
          <button
            type="button"
            data-testid="chart-zoom-reset"
            onClick={reset}
            className="p-2 rounded-md transition-all flex items-center gap-1.5 text-xs"
            style={{
              background: "rgba(225,6,0,0.10)",
              border: "1px solid rgba(225,6,0,0.30)",
              color: "#ff5a55",
            }}
            aria-label="Reset zoom"
          >
            <RotateCcw size={13} />
            <span>{t("calculator.chart_reset")}</span>
          </button>
        </div>
      </div>

      <div className="px-1 mb-4">
        <ZoomRangeSlider value={window} onChange={setClampedWindow} max={TOTAL_MONTHS} />
        <div className="flex justify-between text-[10px] mt-1.5" style={{ color: "#6b6b6b" }}>
          <span>0</span>
          <span>6</span>
          <span>12</span>
          <span>18</span>
          <span>24</span>
        </div>
      </div>

      {/* Stacked charts share one pointer-handler wrapper for drag-pan */}
      <div
        ref={containerRef}
        data-testid="chart-canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="w-full space-y-3"
        style={{
          touchAction: "none",
          cursor: isZoomed ? (dragRef.current.active ? "grabbing" : "grab") : "default",
          userSelect: "none",
        }}
      >
        <MiniChart
          data={visibleData}
          dataKey="invest"
          color={RED}
          title={t("calculator.chart_invest_title")}
          gradientId="investGradient"
          testid="chart-invest"
          t={t}
        />
        <MiniChart
          data={visibleData}
          dataKey="monthly_interest_net"
          color={GOLD}
          title={t("calculator.chart_monthly_title")}
          gradientId="monthlyGradient"
          testid="chart-monthly"
          t={t}
        />
        <MiniChart
          data={visibleData}
          dataKey="cumulative_interest_net"
          color={SILVER}
          title={t("calculator.chart_cumulative_title")}
          gradientId="cumGradient"
          testid="chart-cumulative"
          t={t}
        />
      </div>

      <div className="mt-3 text-[11px]" style={{ color: "#6b6b6b" }}>
        {t("calculator.chart_hint")}
      </div>
    </div>
  );
}
